<template>
  <PageChrome :show-header="false" tight>
    <div class="engagedPage">
      <div v-if="error" class="pageError">{{ error }}</div>
      <div v-if="actionNotice" class="actionNotice" role="status">
        <span>{{ actionNotice }}</span>
        <button class="actionNoticeClose" type="button" aria-label="Dismiss" @click="actionNotice = ''">&times;</button>
      </div>

      <ListToolbar
        noun="candidates"
        :page-size="items.length"
        :selected-count="selectedIds.size"
        :page-fully-selected="allOnPageSelected"
        :total="total"
        :select-all-max="SELECT_ALL_MAX"
        :selecting-all="selectingAll"
        :range-label="rangeLabel"
        :sort="filters.sort"
        :sort-options="SORT_OPTIONS"
        :can-prev="filters.page > 1"
        :can-next="filters.page < pageCount"
        @toggle-page="togglePageSelection"
        @select-none="clearSelection"
        @select-all-matching="selectAllMatching"
        @prev="goToPage(filters.page - 1)"
        @next="goToPage(filters.page + 1)"
        @update:sort="(v: string) => setFilters({ sort: v })"
      >
        <template #filters>
          <input
            v-model="searchText"
            type="text"
            class="searchInput"
            placeholder="Search name, email, or account…"
            @input="onSearchInput"
          />
          <div style="width: 190px">
            <SearchSelect
              :modelValue="filters.play"
              :options="playOptions"
              placeholder="All plays"
              @update:modelValue="(v: string) => setFilters({ play: v })"
            />
          </div>
          <div style="width: 170px">
            <SearchSelect
              :modelValue="filters.owner"
              :options="ownerOptions"
              placeholder="Any owner"
              @update:modelValue="(v: string) => setFilters({ owner: v })"
            />
          </div>
        </template>

        <template #actions>
          <button class="btn btn-sm secondary" type="button" :disabled="bulkBusy" @click="assignOwnerOpen = true">
            Assign owner
          </button>
          <button class="btn btn-sm danger" type="button" @click="openExitModal">Exit play…</button>
        </template>
      </ListToolbar>

      <!-- ── Candidate table ──────────────────────────────────── -->
      <CandidateTableShell
        :rows="items"
        :row-key="(m: any) => m.membership_id"
        :is-selected="(m: any) => selectedIds.has(m.membership_id) || selectedMembershipId === m.membership_id"
        :is-checked="(m: any) => selectedIds.has(m.membership_id)"
        :loading="loading"
        frozen-column-width="260px"
        :grid-template-columns="gridTemplateColumns"
        @open-row="openDrawerFor"
        @toggle-select="toggleSelect"
      >
        <template #frozen-header>
          <div class="headerCol headerNameCol">Name</div>
        </template>
        <template #frozen-row="{ row: m }">
          <div class="nameCol">
            <CandidateRowIdentity :member="{ kind: 'contact', contact_name: m.contact_name, contact_title: m.contact_title }" />
          </div>
        </template>

        <template #header>
          <div class="headerCol">Account</div>
          <div class="headerCol">Reason</div>
          <div class="headerCol">Sentiment</div>
          <div class="headerCol">Play</div>
          <div class="headerCol">Owner</div>
          <div class="headerCol">Changed</div>
          <div class="headerCol">Activity (60d)</div>
        </template>

        <template #row="{ row: m }">
          <div class="accountCol">
            <CandidateRowIdentity :member="{ kind: 'account', account_domain: m.account_domain, account_name: m.account_name }" />
          </div>

          <div class="metaCol">
            <span class="metaPrimary">{{ reasonLabel(m.outcome_reason) }}</span>
            <span v-if="m.outcome_detail" class="metaSecondary metaSecondaryWrap">{{ m.outcome_detail }}</span>
          </div>

          <!-- Sentiment: the synthesized read on where this prospect stands. The
               summary is the point — a bare polarity glyph says nothing a rep can
               act on. -->
          <div class="sentimentCol">
            <div
              v-if="m.engagement"
              class="sentimentCell"
              :class="`sentiment--${m.engagement.polarity}`"
              :title="sentimentTooltip(m.engagement)"
            >
              <span class="sentimentHead">
                <AppIcon :name="sentimentIcon(m.engagement)" :size="12" />
                <span class="sentimentLabel">{{ sentimentLabel(m.engagement) }}</span>
                <span class="sentimentIntensity">{{ m.engagement.intensity }}</span>
              </span>
              <span class="sentimentSummary">{{ sentimentSummary(m.engagement) }}</span>
            </div>
            <span v-else class="metaSecondary">—</span>
          </div>

          <div class="playCol">
            <router-link
              v-if="m.sales_play_id"
              class="n-pill playPill"
              :to="`/sales-plays/${m.sales_play_id}/candidates/people`"
              :title="m.sales_play_name || ''"
              @click.stop
            >
              <AppIcon name="sales_play" :size="12" />
              <span class="playPillText">{{ m.sales_play_name }}</span>
            </router-link>
          </div>

          <div class="ownerCol">
            <span :class="['ownerLabel', { unassigned: !m.owner }]">{{ m.owner?.name || 'Unassigned' }}</span>
          </div>

          <div class="changedCol">
            <span class="metaPrimary" :title="absoluteTime(m.stage_changed_at)">
              {{ relativeTime(m.stage_changed_at) }}
            </span>
          </div>

          <div class="trailingCol" @click.stop>
            <ActivityPillList
              v-if="m.activity?.length"
              :pills="m.activity"
              :on-updated="() => load()"
            />
          </div>
        </template>

        <template #empty>No engaged candidates.</template>
      </CandidateTableShell>
    </div>

    <ExitPlayModal
      v-if="exitModalIds.length > 0"
      :membership-ids="exitModalIds"
      @close="exitModalIds = []"
      @exited="onBulkExited"
    />

    <AssignOwnerModal
      v-if="assignOwnerOpen"
      :selected-count="selectedIds.size"
      :user-options="userOptions"
      :busy="bulkBusy"
      @close="assignOwnerOpen = false"
      @confirm="bulkAssignOwner"
    />

    <template #footer>
      <ShortcutBar :hotkeys="hotkeys" />
    </template>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import ExitPlayModal from '../components/ExitPlayModal.vue'
import AssignOwnerModal from '../components/AssignOwnerModal.vue'
import ShortcutBar from '../components/ShortcutBar.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import CandidateTableShell from '../components/list/CandidateTableShell.vue'
import ListToolbar from '../components/list/ListToolbar.vue'
import CandidateRowIdentity from '../components/CandidateRowIdentity.vue'
import ActivityPillList from '../components/ActivityPillList.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'
import { SELECT_ALL_MAX, SORT_OPTIONS, useCandidateQuery } from '../composables/useCandidateQuery'
import { useCandidateSelection } from '../composables/useCandidateSelection'
import { useCandidateDrawerNav } from '../composables/useCandidateDrawerNav'
import { useChatScope } from '../composables/useChatScope'
import { reasonLabel, absoluteTime, relativeTime } from '../utils/candidates'

/**
 * Engaged people across every play.
 *
 * Same grain and same rows as the Candidates page — membership rows, not
 * contacts — with the play unpinned and the stage pinned to `engaged`. That is
 * what lets it show the reason field at all: `outcome_reason` is a property of
 * the membership, and a contact in three plays has three of them.
 *
 * Contacts only. Engagement is a person act: every path that derives it
 * (inbound reply, dialer disposition, sequence exit) is contact-keyed, and
 * `engagement_signals.contact_id` is NOT NULL. An account membership can only
 * reach `engaged` by a human choosing it in the Exit play modal, which is
 * mislabeling rather than engagement.
 */

const { setScope, clearScope } = useChatScope()

const {
  filters, items, total, loading, error, pageCount, rangeLabel,
  load, fetchAllMatching, setFilters, goToPage, idAfter, idBefore,
} = useCandidateQuery({
  routeName: 'engaged-contacts',
  fixedStage: 'engaged',
  defaultStage: 'engaged',
  fixedKind: 'contact',
  includeActivity: true,
})

const gridTemplateColumns = '220px 220px 220px 140px 120px 100px 260px'

// ── Filter option sources ────────────────────────────────────────

const playOptions = ref<{ value: string; label: string }[]>([])
const userOptions = ref<{ value: string; label: string }[]>([])
const ownerOptions = computed(() => [{ value: '', label: 'Any owner' }, ...userOptions.value])

async function loadFilterOptions() {
  const [plays, users] = await Promise.all([
    apiFetch('/sales-plays').catch(() => null),
    apiFetch('/settings/team_members').catch(() => null),
  ])
  const playRows = Array.isArray(plays) ? plays : (plays?.sales_plays || plays?.plays || [])
  playOptions.value = [
    { value: '', label: 'All plays' },
    ...playRows.map((p: any) => ({ value: String(p.id), label: String(p.name || 'Untitled') })),
  ]
  const userRows = Array.isArray(users) ? users : (users?.users || users?.members || [])
  userOptions.value = userRows.map((u: any) => ({
    value: String(u.user_id || u.id),
    label: String(u.name || u.email || 'Unknown'),
  }))
}

// ── Search (debounced into the URL) ──────────────────────────────

const searchText = ref(filters.value.q)
let searchTimer: ReturnType<typeof setTimeout> | null = null

function onSearchInput() {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => setFilters({ q: searchText.value.trim() }), 300)
}

watch(() => filters.value.q, (q) => { if (q !== searchText.value.trim()) searchText.value = q })

// The composable derives filters from the URL but does not fetch on its own —
// one watcher here owns the reload so selection is cleared in the same tick as
// the filter change. Not `immediate`: the initial fetch is kicked off in
// onMounted instead, because clearSelection() is declared below this point and
// an immediate watcher would run it before its initialization.
watch(
  () => JSON.stringify(filters.value),
  () => {
    clearSelection()
    void load()
  },
)

// ── Selection ────────────────────────────────────────────────────

const bulkBusy = ref(false)
const actionNotice = ref('')

const {
  selectedIds, selectingAll, toggleSelect,
  clearSelection, allLoadedSelected: allOnPageSelected, toggleSelectAll: togglePageSelection,
  selectAllMatching: selectAllMatchingRows,
} = useCandidateSelection<any>({
  items: () => items.value,
  fetchAllMatching,
})

async function selectAllMatching() {
  await selectAllMatchingRows((msg) => { actionNotice.value = msg })
}

// ── Bulk actions ─────────────────────────────────────────────────

const exitModalIds = ref<string[]>([])
const assignOwnerOpen = ref(false)

function openExitModal() {
  exitModalIds.value = [...selectedIds.value]
}

function onBulkExited() {
  exitModalIds.value = []
  clearSelection()
  void load()
}

async function bulkAssignOwner(userId: string) {
  bulkBusy.value = true
  try {
    const result = await apiFetch('/sales-plays/memberships/bulk-assign-owner', {
      method: 'PUT',
      body: { membership_ids: [...selectedIds.value], owner_user_id: userId },
    })
    const skipped = Array.isArray(result?.skipped) ? result.skipped.length : 0
    actionNotice.value = skipped
      ? `Assigned ${result.assigned.length}; ${skipped} skipped (already enrolled in a sequence).`
      : `Assigned ${result?.assigned?.length ?? 0} candidates.`
    assignOwnerOpen.value = false
    clearSelection()
    await load()
  } catch (e: any) {
    actionNotice.value = e?.message || 'Failed to assign owner.'
  } finally {
    bulkBusy.value = false
  }
}

// ── Drawer ───────────────────────────────────────────────────────

const {
  selectedMembershipId, openDrawerFor, hotkeys,
} = useCandidateDrawerNav<any>({
  items: () => items.value,
  playIdFor: (m) => m.sales_play_id,
  idAfter,
  idBefore,
  toggleSelect,
  toggleSelectAll: togglePageSelection,
})

// ── Row helpers ──────────────────────────────────────────────────

const ENGAGEMENT_KIND_LABELS: Record<string, string> = {
  inbound_email: 'Replied',
  agent_human_pickup: 'Answered call',
  agent_voicemail: 'Voicemail left',
  sdr_call: 'Call',
  demo_request: 'Demo request',
  signup: 'Signed up',
  form_submission: 'Form fill',
  identified_web_visit: 'Site visit',
  gong_call: 'Gong call',
}

function sentimentIcon(e: any): string {
  if (e.polarity === 'positive') return 'arrow_up'
  if (e.polarity === 'negative') return 'arrow_down'
  return 'minus'
}

function sentimentLabel(e: any): string {
  return ENGAGEMENT_KIND_LABELS[e.kind] || e.kind
}

/**
 * An inbound reply is born `pending` and the synthesizer fills the summary from
 * the body afterwards, so "still being read" is a real state — saying nothing
 * would make a fresh reply look like it had no content.
 */
function sentimentSummary(e: any): string {
  if (e.summary) return e.summary
  if (e.synthesis_state === 'pending') return 'Reading reply…'
  if (e.synthesis_state === 'failed') return e.title || 'Could not summarize'
  return e.title || ''
}

function sentimentTooltip(e: any): string {
  const parts = [`${e.polarity} · intensity ${e.intensity}`]
  if (e.occurred_at) parts.push(absoluteTime(e.occurred_at))
  return parts.join(' — ')
}

// ── Lifecycle ────────────────────────────────────────────────────

onMounted(() => {
  setScope({ scopeType: 'workspace', scopeSubjectId: '' })
  void load()
  void loadFilterOptions()
})

onUnmounted(() => {
  clearScope()
  if (searchTimer) clearTimeout(searchTimer)
})
</script>

<style scoped>
.engagedPage {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.searchInput {
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  font-size: var(--fs-sm);
  font-family: var(--font-sans);
  background: var(--color-bg-surface);
  color: var(--color-text-primary);
  width: 240px;
  outline: none;
}
.searchInput:focus { border-color: var(--color-accent); }
.searchInput::placeholder { color: var(--color-text-tertiary); }

.pageError {
  padding: var(--space-3);
  margin-bottom: var(--space-2);
  border-radius: var(--radius-md);
  background: var(--color-danger-soft, #fee2e2);
  color: var(--color-danger, #dc2626);
  font-size: var(--fs-sm);
}

.actionNotice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-2);
  border-radius: var(--radius-md);
  background: var(--color-bg-subtle);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
}
.actionNoticeClose {
  border: none;
  background: none;
  font-size: var(--fs-lg);
  line-height: 1;
  color: var(--color-text-tertiary);
  cursor: pointer;
}

/* ── Column header ────────────────────────────────────────── */
.headerCol { min-width: 0; }

.nameCol {
  display: flex;
  align-items: center;
  width: 260px;
  min-width: 0;
}

.accountCol {
  display: flex;
  align-items: center;
  min-width: 0;
}

/* ── Meta / reason columns ─────────────────────────────────── */
.metaCol {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}
.metaPrimary {
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.metaSecondary { font-size: var(--fs-xs); color: var(--color-text-tertiary); }
.metaSecondaryWrap {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Sentiment ───────────────────────────────────────────────── */
.sentimentCol { display: flex; align-items: center; min-width: 0; }
.sentimentCell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  min-width: 0;
  padding: 3px var(--space-2);
}
.sentimentHead {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
}
.sentimentLabel { white-space: nowrap; }
/* Intensity is context for the polarity, not a headline number. */
.sentimentIntensity {
  font-variant-numeric: tabular-nums;
  color: var(--color-text-tertiary);
  font-weight: var(--fw-medium);
}
.sentimentSummary {
  font-size: var(--fs-xs);
  color: var(--color-text-secondary);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.sentiment--positive .sentimentHead { color: var(--color-success, #16a34a); }
.sentiment--negative .sentimentHead { color: var(--color-danger, #dc2626); }
.sentiment--neutral .sentimentHead { color: var(--color-warning, #ca8a04); }

/* ── Play / owner / changed columns ───────────────────────── */
.playCol, .ownerCol, .changedCol {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
/* Same n-pill as the stage cell — a play is a label, not its own visual
   language. Only the link affordance is added.
   `align-self: start` matters: .playCol is a flex column, whose default
   `stretch` would widen the pill to the whole column. */
.playPill {
  align-self: start;
  max-width: 100%;
  text-decoration: none;
  color: inherit;
}
.playPill:hover { border-color: var(--color-accent); }
.playPillText { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.ownerLabel {
  font-size: var(--fs-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.unassigned { color: var(--color-text-tertiary); }

.trailingCol {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}

.assignOwnerBody { display: flex; flex-direction: column; gap: var(--space-3); }
.assignOwnerIntro { font-size: var(--fs-sm); color: var(--color-text-secondary); margin: 0; }
</style>
